// import '@fontsource/lato';
import {
  AmplitudeCookieConsent,
  getEnvConfig,
  theme,
} from '@geneo2-web/shared-ui';
import { ThemeProvider } from '@mui/material';
import * as ReactDOM from 'react-dom/client';

import '@fontsource/lato';
import '@fontsource/poppins';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
// import ManageSession from '../../../libs/shared-ui/src/commonUtils/manageSession';
import { useEffect, useState } from 'react';
import { GlobalContextProvider } from './app/Context/GlobalContextProvider';
import './i18n';
import { persistor, store } from './reduxStore/store';
import { router } from './routeHandling/Routes/Routes';
import {
  DEMO_QUESTION,
  DEMO_RESOURCE_CARD,
  QR_RESOLVER,
} from './routeHandling/RoutesNomenclature';
import registerServiceWorker from './serviceWorkerRegistration';
import * as Sentry from '@sentry/react';
const envConfig = getEnvConfig();

if (
  process.env.NODE_ENV === 'development' &&
  envConfig.environment === 'test'
) {
  import('react-grab');
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

Sentry.init({
  environment: `student-frontend-${process.env.NX_GENEO_ENV}`,
  release: process.env.NX_GENEO_FRONTEND_VERSION,
  dsn: envConfig.sentry.dsn,
  integrations: [
    Sentry.browserTracingIntegration({
      traceFetch: false,
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration({
      mask: [],
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  enabled: envConfig.sentry.enabled,
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [process.env.NX_GENEO_BACKEND_URL || ''],
  // Set profilesSampleRate to 1.0 to profile every transaction.
  // Since profilesSampleRate is relative to tracesSampleRate,
  // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
  // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
  // results in 25% of transactions being profiled (0.5*0.5=0.25)
  profilesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  sendClientReports: envConfig.sentry.enabled, // Disable SDK client reports telemetry
});

const AMPLITUDE_CONSENT_SKIP_PATHS = [
  DEMO_QUESTION,
  DEMO_RESOURCE_CARD,
  QR_RESOLVER,
];

const shouldSkipAmplitudeConsent = (pathname: string) =>
  AMPLITUDE_CONSENT_SKIP_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );

const StudentAmplitudeCookieConsent = () => {
  const [pathname, setPathname] = useState(
    () => router.state.location.pathname
  );

  useEffect(() => {
    return router.subscribe((state) => {
      setPathname(state.location.pathname);
    });
  }, []);

  if (shouldSkipAmplitudeConsent(pathname)) {
    return null;
  }

  return <AmplitudeCookieConsent />;
};

const App = () => {
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'auth' || event.key === 'userId') {
        console.log(
          'Auth / userId key updated in another tab:',
          event.newValue
        );
        // Refresh the page or perform necessary updates
        window.location.reload();
      }
    };

    // Add event listener
    window.addEventListener('storage', handleStorageChange);

    // Cleanup event listener
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <GlobalContextProvider>
          <ThemeProvider theme={theme}>
            <>
              <RouterProvider router={router} />
              <StudentAmplitudeCookieConsent />
            </>
          </ThemeProvider>
        </GlobalContextProvider>
      </PersistGate>
    </Provider>
  );
};

root.render(<App />);

registerServiceWorker();
